import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { PageHeader, Loading, DataTable, SeverityBadge, StatusBadge } from "../components/common";
import AddUseCaseModal from "./AddUseCaseModal";

const INDUSTRIES = [
  ["banking", "Banking & Financial Services"], ["insurance", "Insurance"], ["healthcare", "Healthcare & Hospitals"],
  ["government", "Government & Public Sector"], ["energy", "Energy / Oil & Gas"], ["manufacturing", "Manufacturing & Industrial"],
  ["telecom", "Telecom & ICT"], ["cloud", "Cloud / Data Centre"], ["cyber", "Cybersecurity & SOC"],
  ["legal", "Legal / Judiciary"], ["transport", "Transport & Logistics"], ["retail", "Retail & E-Commerce"],
  ["realestate", "Real Estate & Construction"], ["education", "Education & Universities"], ["tourism", "Tourism / Hospitality"],
  ["agriculture", "Agriculture & Food Security"], ["utilities", "Utilities / Water"], ["hr", "Human Resources"],
  ["procurement", "Procurement / Supply Chain"], ["corpfin", "Corporate Finance / Audit"],
];

export default function UseCases() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [industry, setIndustry] = useState("");
  const [risk, setRisk] = useState("");
  const [lifecycle, setLifecycle] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setRows(null);
    const params = { limit: 1000 };
    if (industry) params.industry = industry;
    if (risk) params.risk = risk;
    if (lifecycle) params.lifecycle = lifecycle;
    api.get("/usecases", { params }).then((r) => setRows(r.data));
  };
  useEffect(load, [industry, risk, lifecycle]);

  const cols = [
    { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs font-semibold text-[var(--qatar)]">{r.id}</span> },
    { key: "name_en", label: t("name"), render: (r) => <span className="font-medium text-slate-800">{r.name_en}</span> },
    { key: "industry", label: t("industry"), render: (r) => <span className="text-xs">{r.industry}</span> },
    { key: "ai_type", label: "AI Type", render: (r) => <span className="text-xs text-slate-500">{r.ai_type}</span> },
    { key: "risk_level", label: t("risk"), render: (r) => <SeverityBadge level={r.risk_level} /> },
    { key: "lifecycle_stage", label: t("lifecycle"), render: (r) => <StatusBadge status={r.lifecycle_stage} /> },
    { key: "red_team_status", label: "Red Team", render: (r) => <StatusBadge status={r.red_team_status} /> },
    { key: "compliance_status", label: "Compliance", render: (r) => <StatusBadge status={r.compliance_status} /> },
  ];

  const Sel = ({ value, onChange, placeholder, options, testid }) => (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 outline-none focus:border-[var(--qatar)]">
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={Array.isArray(o) ? o[0] : o} value={Array.isArray(o) ? o[0] : o}>{Array.isArray(o) ? o[1] : o}</option>)}
    </select>
  );

  return (
    <div>
      <PageHeader title={`${t("nav.usecases")}`} subtitle={rows ? `${rows.length} synthetic AI use cases · 20 industries × 25` : ""} summary="500 synthetic AI use case catalogue across twenty Qatar industries.">
        <button data-testid="add-usecase-btn" onClick={() => setShowAdd(true)} className="qatar-gradient inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white hover:opacity-95">
          <Plus className="h-4 w-4" /> {t("add_usecase")}
        </button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2">
        <Sel testid="filter-industry" value={industry} onChange={setIndustry} placeholder={`${t("all")} ${t("industry")}`} options={INDUSTRIES} />
        <Sel testid="filter-risk" value={risk} onChange={setRisk} placeholder={`${t("all")} ${t("risk")}`} options={["Low", "Medium", "High", "Critical"]} />
        <Sel testid="filter-lifecycle" value={lifecycle} onChange={setLifecycle} placeholder={`${t("all")} ${t("lifecycle")}`} options={["Governance", "AI Policy", "Risk Assessment", "Data Governance", "Security", "Model Governance", "Human Approval", "Compliance", "Continuous Monitoring"]} />
      </div>

      {!rows ? <Loading /> : <DataTable testid="usecases-table" columns={cols} rows={rows} onRowClick={(r) => nav(`/usecases/${r.id}`)} pageSize={25} />}
      {showAdd && <AddUseCaseModal onClose={() => setShowAdd(false)} onCreated={(uc) => { setShowAdd(false); nav(`/usecases/${uc.id}`); }} />}
    </div>
  );
}
